import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  Heading,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Controller, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ContinueAndSkipButton } from '../../common/components/ContinueAndSkipButton';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { COMPONENT_MIN_HEIGHT, TOP_NAV_SPACING_AFFORDANCE } from '../../common/constants';
import { useParamBackUrl } from '../../common/hooks/use-param-back-url';
import { CircleIcon, GridIcon } from '../../styles/custom-icons';
import { trackTariffSkipped } from '../../utils/common/analytics';
import { trackDynatraceTariffSetupStarted, trackDynatraceTariffSkipped } from '../../utils/native/dynatrace-events';
import { selectSite } from '../site/siteSlice';
import { UpdateZipcodeButton } from '../site/UpdateZipcodeButton';
import { RetailerSelect } from './RetailerSelect';
import { useTranslatedDefaultTariffData } from './tariff-constants';
import { useRetailRates } from './tariffApi';
import { useTariffFormContext } from './useTariffFormContext';
import { WattbuyRetailRatesSelect } from './WattbuyRetailRatesSelect';

const COLOR_ICON_GREY = '#9FA0A4';

export const TariffSetup = () => {
  const { errors, setValue, control, setError, reset } = useTariffFormContext();
  const site = useSelector(selectSite);
  const { post_code: postCode, isOnboarding } = site;
  const navigate = useNavigate();
  const utility = useWatch({ name: 'tariff.utility', control });
  const linkButtonColor = useColorModeValue('schneiderSkyBlue.600', 'schneiderSkyBlue.200');
  const { t } = useTranslation();
  const DEFAULT_TARIFF_DATA = useTranslatedDefaultTariffData();
  const backURL = useParamBackUrl(`../../home?direction=back`);
  const toast = useToast({ duration: 3000, isClosable: true });

  // Prefetch retail rates so we know if the user can proceed with auto tariff setup via Wattbuy.
  const { retailRates, isLoading, error: retailRatesError } = useRetailRates();
  const userCanSetupWattbuyTariff = useMemo(() => {
    return !!utility.id && retailRates.length > 0;
  }, [utility, retailRates]);

  useEffect(() => {
    trackDynatraceTariffSetupStarted();
  }, []);

  const handleSubmit = (params = '') => {
    const isUtilityIdInvalid = utility.id == null;
    if (isUtilityIdInvalid) {
      setError('tariff.utility.id', { message: t('Energy Rates.select retailer') });
    } else {
      // If the user has selected a utility that doesnt have any retail rates, they are taken to manual setup
      handleNavigateToManualSetup(params);
    }
  };

  const handleNavigateToManualSetup = (params = '') => {
    // Before we initiate manual setup, reset any form tariff data (can linger after navigating back from auto setup)
    reset({
      ...DEFAULT_TARIFF_DATA,
      tariff: { ...DEFAULT_TARIFF_DATA.tariff, utility },
    });
    setValue('tariff.planName', t('Energy Rates.manual tariff'), { shouldDirty: true });
    navigate(`../tariff-details${params}`);
  };

  const handleSkip = () => {
    trackTariffSkipped();
    trackDynatraceTariffSkipped();
    navigate(backURL);
  };

  useEffect(() => {
    if (!isLoading && retailRatesError && !toast.isActive('retail-rates-fetch-error')) {
      toast({
        title: t('Energy Rates.error fetching retail rates'),
        description: `${t('Common.please try again')} ${t('Energy Rates.if error persists fallback to manual')}`,
        status: 'error',
        id: 'retail-rates-fetch-error',
      });
    }
  }, [isLoading, retailRatesError, t, toast]);

  return (
    <SlidingAnimationPageBase
      title={t('Energy Rates.add your energy rates')}
      includeTopNav
      px={4}
      {...(!isOnboarding ? { backURL } : {})}
      data-testid="tariff-setup"
    >
      <Box display="flex" flexDirection="column" h={`calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE})`}>
        <Box flex={1} minH={0}>
          <Center pt={4} pb={2}>
            <Box position="relative" w="48vw" h="48vw">
              <CircleIcon w="100%" h="100%" color={COLOR_ICON_GREY} data-testid="circle-icon" />
              <GridIcon
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="94px"
                h="96px"
                color={COLOR_ICON_GREY}
                data-testid="grid-icon"
              />
            </Box>
          </Center>

          <Heading textAlign="center" fontSize={24} fontWeight={600}>
            {t('Energy Rates.add your energy provider')}
          </Heading>
          <Text mt={2} textAlign="center" fontSize={16} fontWeight={400}>
            {t('Energy Rates.setup provider description')}
          </Text>

          <Box mt={4}>
            <FormControl isInvalid={!!errors?.tariff?.utility?.id?.message} data-testid="retailer-select">
              <Controller
                control={control}
                name="tariff.utility.name"
                render={() => (
                  <RetailerSelect
                    selectedRetailerId={utility.id}
                    selectedRetailerName={utility.name}
                    postCode={postCode}
                    onSelect={(id, name, state) => {
                      // When retailer changes, reset any form tariff data (can linger after navigating back from auto setup)
                      reset({ ...DEFAULT_TARIFF_DATA });
                      setValue('tariff.utility', { id, name, state }, { shouldDirty: true });
                    }}
                  />
                )}
              />
              <FormErrorMessage mt={1} justifyContent="center">
                {errors?.tariff?.utility?.id?.message}
              </FormErrorMessage>
            </FormControl>
          </Box>

          <Text mt={4} fontSize={16} data-testid="tariff-location-copy">
            <Trans
              i18nKey="Energy Rates.options based on location"
              components={{
                zipcode: (
                  <UpdateZipcodeButton
                    onZipcodeUpdated={(_zipCode) => {
                      reset({ ...DEFAULT_TARIFF_DATA });
                    }}
                    text={postCode}
                  />
                ),
              }}
            />
          </Text>

          <Button
            variant="link"
            fontWeight={400}
            fontSize={15}
            color={linkButtonColor}
            onClick={() => handleNavigateToManualSetup()}
            data-testid="manual-setup-button"
            isLoading={isLoading}
          >
            {t('Energy Rates.add your energy rates')}
          </Button>

          {userCanSetupWattbuyTariff && (
            <Box mt={8}>
              <WattbuyRetailRatesSelect
                showSkip={isOnboarding}
                skipLabel={t('Common.skip').toUpperCase()}
                skipTestId="skip-button"
                onSkip={handleSkip}
              />
            </Box>
          )}
        </Box>

        {!userCanSetupWattbuyTariff && (
          <ContinueAndSkipButton
            showContinue={true}
            showSkip={isOnboarding}
            continueLabel={t('Common.continue')}
            skipLabel={t('Common.skip').toUpperCase()}
            onContinue={() => handleSubmit('?showRatesNotFoundCopy=true')}
            onSkip={handleSkip}
            continueTestId="continue-manual-setup-button"
            skipTestId="skip-button"
            isLoading={isLoading}
          />
        )}
      </Box>
    </SlidingAnimationPageBase>
  );
};
